"use client";
import {
  Autocomplete,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  useTheme,
  Grid,
  ImageList,
  ImageListItem,
  IconButton,
  InputAdornment,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useEffectEvent, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Photo } from "@mui/icons-material";
import { useRequest } from "../functions/request";
import HtmlEditor from "./htmlEditor";

interface dropdownValues {
  id: string;
  value: string;
}

const normalizeDropdownValues = (values: dropdownValues[]) =>
  values.map((option) => ({
    id: String(option.id),
    value: String(option.value),
  }));

export interface options {
  id: string;
  value: string;
}
export interface FormValues {
  label: string;
  type:
    | "TextField"
    | "Number"
    | "Dropdown"
    | "Checkbox"
    | "ExpandedTextField"
    | "HtmlEditor"
    | "Phone"
    | "FileUpload"
    | "DropdownSearch"
    | "Address";
  field: string;
  validationSchema: any;
  placeholder?: string;
  dropdownValues?: dropdownValues[];
  errorText: string | null;
  triggerUnhide?: boolean;
  autoFormat?: "currency" | "currencyRange";
  suffix?: string;
  dropdownSearchFunction?: () => Promise<dropdownValues[]>;
  addNewFunction?: (newValue: string) => Promise<string>;
}

export interface Form {
  FormValues: FormValues[];
  CollectEmail?: boolean;
}

interface Props {
  submit: (payload: any) => void;
  formDef: Form;
  size: number;
  loading: boolean;
  title: string;
  subtitle?: string;
  disabled?: boolean;
  initialValues?: Record<string, unknown>;
}

function isValidEmail(email: string) {
  if (!email || typeof email !== "string") {
    return false;
  }

  // Restricts the end of the email to exactly com, net, or org
  // The 'i' flag at the end makes the check case-insensitive (.COM, .Net, etc.)
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|net|org)$/i;

  return emailRegex.test(email.trim());
}
const formatPhoneNumber = (num: string) => {
  const cleaned = ("" + num).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return num;
};

const maskPhoneInput = (num: string) => {
  const digits = num.replace(/\D/g, "").slice(0, 10);

  if (digits.length <= 3) {
    return digits.length ? `(${digits}` : "";
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatCurrencyValue = (value: string) => {
  if (!value) {
    return "";
  }

  const cleaned = value.replace(/[^\d.]/g, "");
  if (!cleaned) {
    return "";
  }

  const [integerPartRaw = "", decimalPartRaw = ""] = cleaned.split(".");
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "");
  const parsedInteger = Number.parseInt(integerPart || "0", 10);
  const normalizedInteger = Number.isNaN(parsedInteger) ? 0 : parsedInteger;
  const formattedInteger = new Intl.NumberFormat("en-US").format(
    normalizedInteger,
  );

  if (!decimalPartRaw) {
    return `$${formattedInteger}`;
  }

  const normalizedDecimal = decimalPartRaw.slice(0, 2).padEnd(2, "0");
  return `$${formattedInteger}.${normalizedDecimal}`;
};

const formatCurrencyRangeValue = (value: string) => {
  if (!value) {
    return "";
  }

  const parts = value.match(/\d[\d,]*(?:\.\d{0,2})?/g) || [];
  const [firstPart, secondPart] = parts;

  if (!firstPart) {
    return "";
  }

  if (!secondPart) {
    return formatCurrencyValue(firstPart);
  }

  return `${formatCurrencyValue(firstPart)} - ${formatCurrencyValue(secondPart)}`;
};

const acceptedImageTypes = "image/jpeg,image/png,image/webp,image/gif";
const blockedImageExtensions = [".heic", ".heif"];

const isBlockedImageType = (file: File) => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return (
    fileType === "image/heic" ||
    fileType === "image/heif" ||
    blockedImageExtensions.some((extension) => fileName.endsWith(extension))
  );
};

interface DropdownSearchFieldProps {
  item: FormValues;
  value: string;
  error: boolean;
  hasBeenTouched: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}

interface AddressSuggestion {
  place: string;
  placeId: string;
  text: string;
  secondaryText?: string;
}

interface AddressAutocompleteResponse {
  suggestions?: Array<{
    placePrediction?: {
      place?: string;
      placeId?: string;
      text?: { text?: string };
      structuredFormat?: { secondaryText?: { text?: string } };
    };
  }>;
}

interface AddressDetailsResponse {
  formattedAddress?: string;
  location?: { latitude?: number; longitude?: number };
  addressComponents?: Array<{ types?: string[] }>;
}

interface AddressFieldProps {
  item: FormValues;
  value: string;
  error: boolean;
  hasBeenTouched: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}

const AddressField = ({
  item,
  value,
  error,
  hasBeenTouched,
  disabled,
  onChange,
}: AddressFieldProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const placesApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!placesApiKey || inputValue.trim().length < 3 || disabled) {
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          "https://places.googleapis.com/v1/places:autocomplete",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": placesApiKey,
              "X-Goog-FieldMask":
                "suggestions.placePrediction.place,suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat.secondaryText.text",
            },
            body: JSON.stringify({
              input: inputValue,
              includedRegionCodes: ["us"],
              languageCode: "en",
            }),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          setSuggestions([]);
          return;
        }

        const data = (await response.json()) as AddressAutocompleteResponse;
        setSuggestions(
          (data.suggestions || [])
            .flatMap((entry): AddressSuggestion[] => {
              const prediction = entry.placePrediction;
              if (
                !prediction?.place ||
                !prediction.placeId ||
                !prediction.text?.text
              ) {
                return [];
              }
              return [
                {
                  place: prediction.place,
                  placeId: prediction.placeId,
                  text: prediction.text.text,
                  secondaryText:
                    prediction.structuredFormat?.secondaryText?.text,
                },
              ];
            })
        );
      } catch {
        if (!controller.signal.aborted) {
          setSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [disabled, inputValue, placesApiKey]);

  const selectAddress = async (selection: AddressSuggestion | null) => {
    if (!selection) {
      onChange("");
      setAddressError("Select an address from suggestions to verify it.");
      return;
    }

    setIsLoading(true);
    setAddressError(null);
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/${selection.place}`,
        {
          headers: {
            "X-Goog-Api-Key": placesApiKey,
            "X-Goog-FieldMask": "formattedAddress,location,addressComponents",
          },
        },
      );
      const details = (await response.json()) as AddressDetailsResponse;
      const required = [
        "route",
        "locality",
        "administrative_area_level_1",
        "postal_code",
        "country",
      ];
      const isComplete =
        response.ok &&
        Boolean(details.formattedAddress && details.location) &&
        required.every((type) =>
          details.addressComponents?.some((component) =>
            component.types?.includes(type),
          ),
        );

      if (!isComplete) {
        onChange("");
        setAddressError("Select a complete street address from suggestions.");
        return;
      }

      setInputValue(details.formattedAddress!);
      onChange(details.formattedAddress!);
    } catch {
      onChange("");
      setAddressError("Unable to verify this address right now.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Autocomplete
      options={suggestions}
      inputValue={inputValue}
      getOptionLabel={(option) => option.text}
      loading={isLoading}
      disabled={disabled || !placesApiKey}
      filterOptions={(options) => options}
      onChange={(_, selection) => void selectAddress(selection)}
      onInputChange={(_, nextValue, reason) => {
        if (reason !== "input") return;
        setInputValue(nextValue);
        setSuggestions([]);
        setAddressError(null);
        onChange("");
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.placeId}>
          <Box>
            <Box>{option.text}</Box>
            {option.secondaryText ? (
              <Box sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                {option.secondaryText}
              </Box>
            ) : null}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={item.placeholder || item.label}
          placeholder="123 Main St, City, State ZIP"
          error={hasBeenTouched && (error || Boolean(addressError))}
          helperText={
            addressError ||
            (hasBeenTouched && error
              ? item.errorText
              : "Select from suggestions to verify.")
          }
        />
      )}
    />
  );
};

const DropdownSearchField = ({
  item,
  value,
  error,
  hasBeenTouched,
  disabled,
  onChange,
}: DropdownSearchFieldProps) => {
  const [dropdownValues, setDropdownValues] = useState<dropdownValues[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const loadDropdownValues = useEffectEvent(async () => {
    return (await item.dropdownSearchFunction?.()) ?? [];
  });
  const syncDropdownValue = useEffectEvent(
    (values: dropdownValues[], currentValue: string) => {
      if (values.some((option) => option.id === currentValue)) {
        return;
      }

      const matchingOption = values.find(
        (option) => option.value === currentValue,
      );
      if (matchingOption) {
        onChange(matchingOption.id);
      }
    },
  );

  useEffect(() => {
    let isActive = true;

    setIsLoading(true);
    void loadDropdownValues()
      .then((values) => {
        if (isActive) {
          setDropdownValues(normalizeDropdownValues(values));
        }
      })
      .catch(() => {
        if (isActive) {
          setDropdownValues([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [item.field]);

  useEffect(() => {
    if (dropdownValues.length > 0 && value) {
      syncDropdownValue(dropdownValues, value);
    }
  }, [dropdownValues, value]);

  const closeAddDialog = () => {
    if (isAdding) {
      return;
    }

    setIsAddDialogOpen(false);
    setNewValue("");
    setAddError("");
  };

  const addNewValue = async () => {
    const name = newValue.trim();
    if (!name || !item.addNewFunction) {
      return;
    }

    setIsAdding(true);
    setAddError("");
    try {
      const createdId = await item.addNewFunction(name);
      setIsLoading(true);
      setDropdownValues(normalizeDropdownValues(await loadDropdownValues()));
      onChange(createdId);
      setIsAddDialogOpen(false);
      setNewValue("");
    } catch {
      setAddError(
        `Unable to add ${item.label.toLowerCase()}. Please try again.`,
      );
    } finally {
      setIsAdding(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <FormControl fullWidth error={hasBeenTouched && error}>
          <InputLabel>{item.placeholder || item.label}</InputLabel>
          <Select
            label={item.placeholder || item.label}
            value={value || ""}
            disabled={disabled || isLoading}
            endAdornment={
              isLoading ? (
                <CircularProgress
                  size={20}
                  aria-label={`Loading ${item.label} options`}
                  sx={{ mr: 3 }}
                />
              ) : undefined
            }
            onChange={(event) => onChange(String(event.target.value))}
          >
            {dropdownValues.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {error && hasBeenTouched ? item.errorText : " "}
          </FormHelperText>
        </FormControl>
        {item.addNewFunction && (
          <IconButton
            type="button"
            aria-label={`Add new ${item.label}`}
            title={`Add new ${item.label}`}
            disabled={disabled || isLoading}
            onClick={() => setIsAddDialogOpen(true)}
            sx={{ mt: 1 }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      <Dialog
        open={isAddDialogOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth="xs"
      >
        <Box>
          <DialogTitle>Add new {item.label.toLowerCase()}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Name"
              value={newValue}
              disabled={isAdding}
              error={Boolean(addError)}
              helperText={addError || " "}
              onChange={(event) => {
                setNewValue(event.target.value);
                setAddError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void addNewValue();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAddDialog} disabled={isAdding}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isAdding || !newValue.trim()}
              onClick={() => void addNewValue()}
            >
              {isAdding ? <CircularProgress size={20} /> : "Add"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

const FormTemplate = ({
  submit,
  formDef,
  size,
  loading,
  title,
  subtitle,
  disabled = false,
  initialValues,
}: Props) => {
  const theme = useTheme();
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [hideForm, setHideForm] = useState(
    formDef.FormValues.find((e) => e.triggerUnhide === true)?.triggerUnhide ===
      true
      ? false
      : true,
  );
  const hasValidationError = (field: string, value: any) => {
    try {
      formDef.FormValues.find(
        (e) => e.field === field,
      )?.validationSchema.validateSync(value);
      return false;
    } catch {
      return true;
    }
  };
  const call = useRequest();
  const buildInitialFormValues = () =>
    formDef.FormValues.reduce(
      (acc, element) => {
        const providedInitialValue = initialValues?.[element.field];
        switch (element.type) {
          case "TextField":
          case "Phone":
          case "ExpandedTextField":
          case "HtmlEditor":
          case "Address": {
            const value =
              typeof providedInitialValue === "string"
                ? providedInitialValue
                : "";
            acc[element.field] = {
              value,
              error: hasValidationError(element.field, value),
            };
            break;
          }
          case "Number": {
            const value =
              typeof providedInitialValue === "number" ||
              typeof providedInitialValue === "string"
                ? String(providedInitialValue)
                : "";
            acc[element.field] = {
              value,
              error: hasValidationError(element.field, value),
            };
            break;
          }
          case "Checkbox": {
            const value =
              typeof providedInitialValue === "boolean"
                ? providedInitialValue
                : false;
            acc[element.field] = {
              value,
              error: hasValidationError(element.field, value),
            };
            break;
          }
          case "Dropdown":
          case "DropdownSearch": {
            const value =
              typeof providedInitialValue === "string"
                ? providedInitialValue
                : "";
            acc[element.field] = {
              value,
              error: hasValidationError(element.field, value),
            };
            break;
          }
          case "FileUpload": {
            const value = Array.isArray(providedInitialValue)
              ? providedInitialValue
              : [];
            acc[element.field] = {
              value,
              error: hasValidationError(element.field, value),
            };
            break;
          }
        }
        return acc;
      },
      {} as Record<string, any>,
    );

  const [formValues, setFormValues] = useState(buildInitialFormValues);

  useEffect(() => {
    setFormValues(buildInitialFormValues());
    setHasBeenTouched(false);
  }, [initialValues]);

  const formatFieldValueForPayload = (field: string, value: any) => {
    const fieldDefinition = formDef.FormValues.find((e) => e.field === field);

    if (fieldDefinition?.type === "Phone") {
      return String(value).replace(/\D/g, "").slice(0, 10);
    }

    if (fieldDefinition?.autoFormat === "currency") {
      return formatCurrencyValue(String(value));
    }

    if (fieldDefinition?.autoFormat === "currencyRange") {
      return formatCurrencyRangeValue(String(value));
    }

    return value;
  };

  const onSubmit = (data: any): any => {
    if (disabled) {
      return;
    }
    const payload = Object.entries(data).map(([key, obj]) => [
      key,
      formatFieldValueForPayload(key, (obj as { value: string }).value),
    ]);
    submit(Object.fromEntries(payload));
  };
  const collectEmail = async (email: string) => {
    const result = await call("public/collectEmail", "POST", { email });
  };
  const setValue = (value: any, label: string) => {
    try {
      if (label === "email" && isValidEmail(value) && formDef.CollectEmail) {
        collectEmail(value);
      }
      formDef.FormValues.find(
        (e) => e.field === label,
      )?.validationSchema.validateSync(value);
      setFormValues((prevState) => ({
        ...prevState,
        [label]: { value, error: false },
      }));
    } catch (error) {
      setFormValues((prevState) => ({
        ...prevState,
        [label]: { value, error: true },
      }));
    }
  };

  const findErrors = () => {
    for (const key in formValues) {
      if (formValues[key].error !== false) {
        return true;
      }
    }
    return false;
  };

  return (
    <Box className="lightForm width75">
      <h2 className="heading mb20">{title}</h2>
      {subtitle && <p className="subheading mb20">{subtitle}</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formValues);
        }}
        noValidate
      >
        <Grid container spacing={2}>
          <Grid size={size}>
            {formDef.FormValues.map((item) =>
              item.type === "TextField" ||
              item.type === "Phone" ||
              item.type === "Number" ? (
                item.triggerUnhide === false && hideForm === false ? null : (
                  <TextField
                    key={item.field}
                    label={
                      item.type === "Phone"
                        ? formatPhoneNumber(item.placeholder || "")
                        : item.placeholder || `Enter ${item.label}`
                    }
                    type={item.type === "Number" ? "number" : "text"}
                    variant="outlined"
                    fullWidth
                    error={
                      hasBeenTouched && (formValues[item.field]?.error || false)
                    }
                    value={formValues[item.field]?.value}
                    disabled={disabled}
                    helperText={
                      formValues[item.field]?.error && hasBeenTouched
                        ? item.errorText
                        : " "
                    }
                    onChange={(e) => {
                      const nextValue =
                        item.type === "Phone"
                          ? maskPhoneInput(e.target.value)
                          : e.target.value;
                      setValue(nextValue, item.field);
                      if (item.triggerUnhide === true && hideForm === false) {
                        setHideForm(true);
                      } else {
                        setHasBeenTouched(true);
                      }
                    }}
                    onBlur={(e) => {
                      if (item.autoFormat === "currency") {
                        setValue(
                          formatCurrencyValue(e.target.value),
                          item.field,
                        );
                        return;
                      }

                      if (item.autoFormat === "currencyRange") {
                        setValue(
                          formatCurrencyRangeValue(e.target.value),
                          item.field,
                        );
                      }
                    }}
                    slotProps={
                      item.suffix ||
                      item.type === "Phone" ||
                      item.type === "Number"
                        ? {
                            input: item.suffix
                              ? {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {item.suffix}
                                    </InputAdornment>
                                  ),
                                }
                              : undefined,
                            htmlInput:
                              item.type === "Phone"
                                ? { inputMode: "tel", maxLength: 14 }
                                : item.type === "Number"
                                  ? { inputMode: "decimal" }
                                  : undefined,
                          }
                        : undefined
                    }
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "black", // or "" if you want default
                      },
                    }}
                  />
                )
              ) : item.type === "Checkbox" ? (
                <FormControlLabel
                  key={item.field}
                  control={
                    <Checkbox
                      // 1. Change 'value' to 'checked' to control the checkbox state
                      checked={!!formValues[item.field].value}
                      disabled={disabled}
                      // 2. Change 'e.target.value' to 'e.target.checked'
                      onChange={(e) => {
                        setHasBeenTouched(true);
                        setValue(e.target.checked, item.field);
                      }}
                    />
                  }
                  label={item.label}
                />
              ) : item.type === "Dropdown" ? (
                <FormControl
                  key={item.field}
                  fullWidth
                  error={
                    hasBeenTouched && (formValues[item.field]?.error || false)
                  }
                >
                  <InputLabel>{item.placeholder || item.label}</InputLabel>
                  <Select
                    label={item.placeholder || item.label}
                    value={formValues[item.field]?.value || ""}
                    disabled={disabled}
                    onChange={(e) => {
                      setHasBeenTouched(true);
                      setValue(String(e.target.value), item.field);
                    }}
                  >
                    {item.dropdownValues?.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formValues[item.field]?.error && hasBeenTouched
                      ? item.errorText
                      : " "}
                  </FormHelperText>
                </FormControl>
              ) : item.type === "ExpandedTextField" ? (
                item.triggerUnhide === false && hideForm === false ? null : (
                  <TextField
                    key={item.field}
                    label={item.placeholder || `Enter ${item.label}`}
                    variant="outlined"
                    fullWidth
                    error={
                      hasBeenTouched && (formValues[item.field]?.error || false)
                    }
                    value={formValues[item.field]?.value}
                    disabled={disabled}
                    helperText={
                      formValues[item.field]?.error && hasBeenTouched
                        ? item.errorText
                        : " "
                    }
                    onChange={(e) => {
                      setHasBeenTouched(true);
                      setValue(e.target.value, item.field);
                    }}
                    multiline
                    rows={6}
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "black", // or "" if you want default
                      },
                    }}
                  />
                )
              ) : item.type === "HtmlEditor" ? (
                item.triggerUnhide === false && hideForm === false ? null : (
                  <HtmlEditor
                    key={item.field}
                    label={item.placeholder || item.label}
                    value={formValues[item.field]?.value || ""}
                    disabled={disabled}
                    error={
                      hasBeenTouched && (formValues[item.field]?.error || false)
                    }
                    helperText={item.errorText}
                    onChange={(value) => {
                      setHasBeenTouched(true);
                      setValue(value, item.field);
                    }}
                  />
                )
              ) : item.type === "FileUpload" ? (
                item.triggerUnhide === false && hideForm === false ? null : (
                  <Box key={item.field}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <label
                        className="image-upload"
                        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                      >
                        <FileUploadIcon />{" "}
                        {item.placeholder || `Upload ${item.label}`}
                        <input
                          type="file"
                          hidden
                          disabled={disabled}
                          accept={acceptedImageTypes}
                          onChange={async (e) => {
                            setHasBeenTouched(true);
                            const file = e.target.files?.[0];
                            if (!file) {
                              return;
                            }

                            if (isBlockedImageType(file)) {
                              e.target.value = "";
                              return;
                            }

                            // Convert file → imageData
                            const toimageData = (file: File) =>
                              new Promise<string>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () =>
                                  resolve(reader.result as string);
                                reader.onerror = reject;
                                reader.readAsDataURL(file);
                              });

                            const imageData = await toimageData(file);

                            const imageObj = {
                              imageData,
                              name: file.name,
                              type: file.type,
                            };

                            setValue(
                              [...formValues[item.field]?.value, imageObj],
                              item.field,
                            );
                          }}
                        />{" "}
                      </label>
                      {hasBeenTouched &&
                      formValues[item.field]?.error === true ? (
                        <p style={{ color: "red", marginLeft: "10px" }}>
                          {item.errorText}
                        </p>
                      ) : null}
                    </Box>
                    <div className="image-preview">
                      {formValues[item.field]?.value.length === 0 ? (
                        <>
                          <Photo
                            sx={{
                              fontSize: 80,
                              color: theme.palette.grey[400],
                              display: "flex",
                              justifySelf: "center",
                              mt: 2,
                            }}
                          />
                          <p className="no-photos">No photos uploaded</p>
                        </>
                      ) : (
                        <ImageList
                          cols={3}
                          rowHeight={160}
                          sx={{ mt: 2, overflow: "scroll" }}
                        >
                          {formValues[item.field]?.value.map(
                            (
                              photo: {
                                imageData: string;
                                name: string;
                                type: string;
                              },
                              index: number,
                            ) => (
                              <ImageListItem
                                key={`${photo.name}-${index}`}
                                sx={{
                                  position: "relative",
                                  width: 160,
                                  height: 160,
                                  border: "2px solid black",
                                }}
                              >
                                <IconButton
                                  size="small"
                                  disabled={disabled}
                                  onClick={() => {
                                    setHasBeenTouched(true);
                                    const updated = formValues[
                                      item.field
                                    ]?.value.filter(
                                      (_: unknown, i: number) => i !== index,
                                    );
                                    setValue(updated, item.field);
                                  }}
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    zIndex: 2,
                                    backgroundColor: "rgba(255,255,255,0.9)",
                                    border: "1px solid black",
                                    p: "2px",
                                    "&:hover": {
                                      backgroundColor: "white",
                                    },
                                  }}
                                  aria-label={`Remove image ${index + 1}`}
                                >
                                  <CloseIcon
                                    sx={{ fontSize: 14, color: "black" }}
                                  />
                                </IconButton>
                                <img
                                  src={photo.imageData}
                                  alt={`upload-${index}`}
                                  loading="lazy"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              </ImageListItem>
                            ),
                          )}
                        </ImageList>
                      )}
                    </div>
                  </Box>
                )
              ) : item.type === "DropdownSearch" ? (
                <DropdownSearchField
                  key={item.field}
                  item={item}
                  value={formValues[item.field]?.value || ""}
                  error={formValues[item.field]?.error || false}
                  hasBeenTouched={hasBeenTouched}
                  disabled={disabled}
                  onChange={(value) => {
                    setHasBeenTouched(true);
                    setValue(value, item.field);
                  }}
                />
              ) : item.type === "Address" ? (
                <AddressField
                  key={item.field}
                  item={item}
                  value={formValues[item.field]?.value || ""}
                  error={formValues[item.field]?.error || false}
                  hasBeenTouched={hasBeenTouched}
                  disabled={disabled}
                  onChange={(value) => {
                    setHasBeenTouched(true);
                    setValue(value, item.field);
                  }}
                />
              ) : null,
            )}
          </Grid>
        </Grid>

        <Box sx={{ width: "100%" }}>
          {loading === true ? (
            <CircularProgress />
          ) : (
            <button
              type="submit"
              className="submitButton"
              disabled={disabled || findErrors() || false}
            >
              {initialValues ? "Update" : "Submit"}
            </button>
          )}
        </Box>
      </form>
    </Box>
  );
};

export default FormTemplate;
